// ApiServiceParent.js - Service API pour le tableau de bord parent
import { ENV_CONFIG } from '../config/environment';
import { ApiServiceShared } from './ApiServiceShared';
import { dateStrInTz, formatTimeInTz, getAppTimezone } from '../utils/dateTime';

class ApiServiceParent {
  constructor() {
    this.baseUrl = ENV_CONFIG.apiBaseUrl;
    this.token = null;
    this.authErrorHandler = null; // Handler pour les erreurs d'authentification
  }

  static getInstance() {
    if (!ApiServiceParent.instance) {
      ApiServiceParent.instance = new ApiServiceParent();
    }
    // S'assurer que l'instance a bien toutes les méthodes
    if (!ApiServiceParent.instance.logout) {
      ApiServiceParent.instance.logout = function() {
        this.token = null;
        localStorage.removeItem('app_token');
        localStorage.removeItem('app_user');
        localStorage.removeItem('login_timestamp');
        console.log('✅ PARENT API - Déconnexion effectuée');
      };
    }
    return ApiServiceParent.instance;
  }

  // Méthode pour définir le handler d'erreur d'authentification
  setAuthErrorHandler(handler) {
    this.authErrorHandler = handler;
  }

  // Méthode pour s'assurer que le token est présent
  _ensureToken() {
    if (!this.token) {
      this.token = localStorage.getItem('app_token');
      console.log('🔍 PARENT API - Token récupéré depuis localStorage:', this.token ? 'Présent' : 'Absent');
    }
    
    if (!this.token) {
      console.log('❌ PARENT API - Aucun token trouvé');
      console.log('🔍 PARENT API - localStorage app_token:', localStorage.getItem('app_token'));
      console.log('🔍 PARENT API - localStorage app_user:', localStorage.getItem('app_user'));
    } else {
      console.log('✅ PARENT API - Token présent:', this.token.substring(0, 20) + '...');
    }
    
    return this.token;
  }

  // Méthode pour gérer les erreurs d'authentification et rediriger vers la page d'accueil
  _handleAuthError(response, errorMessage) {
    // Vérifier si c'est une erreur 401 (Token d'accès requis)
    if (response && response.status === 401) {
      console.error('❌ PARENT API - Erreur d\'authentification (401):', errorMessage);
      console.log('🔄 PARENT API - Nettoyage du localStorage et redirection vers la page d\'accueil');
      
      // Nettoyer le localStorage
      localStorage.removeItem('app_token');
      localStorage.removeItem('app_user');
      localStorage.removeItem('login_timestamp');
      
      // Utiliser le handler personnalisé si disponible, sinon fallback sur reload
      if (this.authErrorHandler && typeof this.authErrorHandler === 'function') {
        console.log('✅ PARENT API - Utilisation du handler de navigation personnalisé');
        this.authErrorHandler();
      } else {
        console.log('⚠️ PARENT API - Handler non disponible, utilisation de window.location.reload()');
        window.location.reload();
      }
      
      return true; // Indique qu'une redirection a été effectuée
    }
    return false; // Pas d'erreur d'authentification
  }

  // Méthode utilitaire pour décoder le token et extraire les infos
  _decodeToken() {
    let parentIdFromToken = null;
    let decodedTokenInfo = null;
    
    if (this.token) {
      try {
        const parts = this.token.split('.');
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
          parentIdFromToken = payload.id || payload.user_id || payload.userId;
          decodedTokenInfo = {
            id: parentIdFromToken,
            username: payload.username,
            type: payload.type
          };
        }
      } catch (decodeError) {
        // Ignorer l'erreur de décodage
      }
    }
    
    return { parentIdFromToken, decodedTokenInfo };
  }

  // ==================== PARENT PROFILE ====================
  
  // Récupérer le profil du parent
  async getParentProfile() {
    try {
      this._ensureToken();
      
      const { parentIdFromToken, decodedTokenInfo } = this._decodeToken();
      const currentUser = this.getCurrentUser();
      const parentIdFromStorage = currentUser?.id || currentUser?.user_id || currentUser?.userId;
      
      const url = `${this.baseUrl}/api/parent/profile`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` })
      };
      
      // Logs des paramètres en un seul objet JSON
      const requestParams = {
        method: 'GET',
        url: url,
        headers: headers,
        token_present: !!this.token,
        token_preview: this.token ? this.token.substring(0, 20) + '...' : null,
        parent_id_from_token: parentIdFromToken,
        parent_id_from_storage: parentIdFromStorage,
        decoded_token: decodedTokenInfo,
        base_url: this.baseUrl,
        parameters: null,
        note: 'Le backend extrait automatiquement parentUserId depuis req.user.id du token JWT'
      };
      console.log('🔍 PARENT API - getParentProfile - Paramètres envoyés:', JSON.stringify(requestParams, null, 2));
      
      let response;
      let responseText;
      let data;
      
      try {
        response = await fetch(url, {
          method: 'GET',
          headers: headers
        });
        
        responseText = await response.text();
        
        const rawResponseData = {
          status: response.status,
          status_text: response.statusText,
          response_ok: response.ok,
          headers: Object.fromEntries(response.headers.entries()),
          response_text: responseText
        };
        console.log('🔍 PARENT API - getParentProfile - Réponse brute:', JSON.stringify(rawResponseData, null, 2));
        
        try {
          data = responseText ? JSON.parse(responseText) : null;
        } catch (parseError) {
          const parseErrorData = {
            error: 'Erreur lors du parsing JSON',
            parse_error: parseError.message,
            response_text: responseText,
            status: response.status
          };
          console.error('❌ PARENT API - getParentProfile - Erreur parsing JSON:', JSON.stringify(parseErrorData, null, 2));
          throw new Error(`Réponse non-JSON du serveur (status: ${response.status}): ${responseText.substring(0, 100)}`);
        }
      } catch (fetchError) {
        const fetchErrorData = {
          error: 'Erreur lors de l\'appel fetch',
          fetch_error: fetchError.message,
          error_type: fetchError.constructor.name,
          url: url,
          network_error: fetchError instanceof TypeError && fetchError.message.includes('fetch')
        };
        console.error('❌ PARENT API - getParentProfile - Erreur fetch:', JSON.stringify(fetchErrorData, null, 2));
        throw fetchError;
      }

      // Logs de la réponse en un seul objet JSON
      const responseData = {
        status: response.status,
        response_ok: response.ok,
        response_body: data,
        data_count: data?.data ? (Array.isArray(data.data) ? data.data.length : 1) : 0
      };
      console.log('🔍 PARENT API - getParentProfile - Réponse reçue:', JSON.stringify(responseData, null, 2));

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401) {
          this._handleAuthError(response, data?.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        const errorMessage = data?.message || data?.error || response.statusText || `HTTP error! status: ${response.status}`;
        const httpErrorData = {
          status: response.status,
          status_text: response.statusText,
          error_message: errorMessage,
          response_body: data
        };
        console.error('❌ PARENT API - getParentProfile - Erreur HTTP:', JSON.stringify(httpErrorData, null, 2));
        throw new Error(errorMessage);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      const errorData = {
        error: error.message,
        error_type: error.constructor.name,
        stack: error.stack,
        name: error.name
      };
      console.error('❌ PARENT API - getParentProfile - Erreur:', JSON.stringify(errorData, null, 2));
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération du profil'
      };
    }
  }

  // ==================== PARENT STUDENTS ====================
  
  // Récupérer les étudiants du parent connecté
  async getParentStudents(forceRefresh = false) {
    try {
      this._ensureToken();
      
      const { parentIdFromToken, decodedTokenInfo } = this._decodeToken();
      const currentUser = this.getCurrentUser();
      const parentIdFromStorage = currentUser?.id || currentUser?.user_id || currentUser?.userId;
      
      // Ajouter un paramètre de cache-busting si on force le refresh
      const cacheBuster = forceRefresh ? `?_t=${Date.now()}` : '';
      const url = `${this.baseUrl}/api/parent/students${cacheBuster}`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` }),
        // Forcer le rechargement en désactivant le cache du navigateur
        ...(forceRefresh && { 'Cache-Control': 'no-cache, no-store, must-revalidate', 'Pragma': 'no-cache', 'Expires': '0' })
      };
      
      // Logs des paramètres en un seul objet JSON
      const requestParams = {
        method: 'GET',
        url: url,
        headers: headers,
        token_present: !!this.token,
        token_preview: this.token ? this.token.substring(0, 20) + '...' : null,
        parent_id_from_token: parentIdFromToken,
        parent_id_from_storage: parentIdFromStorage,
        decoded_token: decodedTokenInfo,
        base_url: this.baseUrl,
        parameters: null,
        note: 'Le backend extrait automatiquement parentUserId depuis req.user.id du token JWT'
      };
      console.log('🔍 PARENT API - getParentStudents - Paramètres envoyés:', JSON.stringify(requestParams, null, 2));
      
      let response;
      let responseText;
      let data;
      
      try {
        response = await fetch(url, {
          method: 'GET',
          headers: headers,
          // Forcer le rechargement en désactivant le cache du navigateur
          cache: forceRefresh ? 'no-store' : 'default'
        });
        
        responseText = await response.text();
        
        const rawResponseData = {
          status: response.status,
          status_text: response.statusText,
          response_ok: response.ok,
          headers: Object.fromEntries(response.headers.entries()),
          response_text: responseText
        };
        console.log('🔍 PARENT API - getParentStudents - Réponse brute:', JSON.stringify(rawResponseData, null, 2));
        
        try {
          data = responseText ? JSON.parse(responseText) : null;
        } catch (parseError) {
          const parseErrorData = {
            error: 'Erreur lors du parsing JSON',
            parse_error: parseError.message,
            response_text: responseText,
            status: response.status
          };
          console.error('❌ PARENT API - getParentStudents - Erreur parsing JSON:', JSON.stringify(parseErrorData, null, 2));
          throw new Error(`Réponse non-JSON du serveur (status: ${response.status}): ${responseText.substring(0, 100)}`);
        }
      } catch (fetchError) {
        const fetchErrorData = {
          error: 'Erreur lors de l\'appel fetch',
          fetch_error: fetchError.message,
          error_type: fetchError.constructor.name,
          url: url,
          network_error: fetchError instanceof TypeError && fetchError.message.includes('fetch')
        };
        console.error('❌ PARENT API - getParentStudents - Erreur fetch:', JSON.stringify(fetchErrorData, null, 2));
        throw fetchError;
      }

      // Logs de la réponse en un seul objet JSON
      const responseData = {
        status: response.status,
        response_ok: response.ok,
        response_body: data,
        data_count: data?.data ? (Array.isArray(data.data) ? data.data.length : 0) : 0
      };
      console.log('🔍 PARENT API - getParentStudents - Réponse reçue:', JSON.stringify(responseData, null, 2));

      if (!response.ok) {
        const errorMessage = data?.message || data?.error || response.statusText || `HTTP error! status: ${response.status}`;
        const httpErrorData = {
          status: response.status,
          status_text: response.statusText,
          error_message: errorMessage,
          response_body: data
        };
        console.error('❌ PARENT API - getParentStudents - Erreur HTTP:', JSON.stringify(httpErrorData, null, 2));
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401) {
          this._handleAuthError(response, errorMessage);
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(errorMessage);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      const errorData = {
        error: error.message,
        error_type: error.constructor.name,
        stack: error.stack,
        name: error.name
      };
      console.error('❌ PARENT API - getParentStudents - Erreur:', JSON.stringify(errorData, null, 2));
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des étudiants'
      };
    }
  }

  // ==================== PARENT GRADES ====================
  
  // Récupérer les notes des enfants du parent
  async getParentGrades(lang = 'ar') {
    try {
      this._ensureToken();
      
      const { parentIdFromToken, decodedTokenInfo } = this._decodeToken();
      const currentUser = this.getCurrentUser();
      const parentIdFromStorage = currentUser?.id || currentUser?.user_id || currentUser?.userId;
      const locale = lang === 'fr' || lang === 'en' ? lang : 'ar';
      
      const url = `${this.baseUrl}/api/parent/grades?lang=${encodeURIComponent(locale)}`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` })
      };
      
      // Logs des paramètres en un seul objet JSON
      const requestParams = {
        method: 'GET',
        url: url,
        headers: headers,
        token_present: !!this.token,
        token_preview: this.token ? this.token.substring(0, 20) + '...' : null,
        parent_id_from_token: parentIdFromToken,
        parent_id_from_storage: parentIdFromStorage,
        decoded_token: decodedTokenInfo,
        base_url: this.baseUrl,
        parameters: null,
        note: 'Le backend extrait automatiquement parentUserId depuis req.user.id du token JWT'
      };
      console.log('🔍 PARENT API - getParentGrades - Paramètres envoyés:', JSON.stringify(requestParams, null, 2));
      
      let response;
      let responseText;
      let data;
      
      try {
        response = await fetch(url, {
          method: 'GET',
          headers: headers
        });
        
        responseText = await response.text();
        
        const rawResponseData = {
          status: response.status,
          status_text: response.statusText,
          response_ok: response.ok,
          headers: Object.fromEntries(response.headers.entries()),
          response_text: responseText
        };
        console.log('🔍 PARENT API - getParentGrades - Réponse brute:', JSON.stringify(rawResponseData, null, 2));
        
        try {
          data = responseText ? JSON.parse(responseText) : null;
        } catch (parseError) {
          const parseErrorData = {
            error: 'Erreur lors du parsing JSON',
            parse_error: parseError.message,
            response_text: responseText,
            status: response.status
          };
          console.error('❌ PARENT API - getParentGrades - Erreur parsing JSON:', JSON.stringify(parseErrorData, null, 2));
          throw new Error(`Réponse non-JSON du serveur (status: ${response.status}): ${responseText.substring(0, 100)}`);
        }
      } catch (fetchError) {
        const fetchErrorData = {
          error: 'Erreur lors de l\'appel fetch',
          fetch_error: fetchError.message,
          error_type: fetchError.constructor.name,
          url: url,
          network_error: fetchError instanceof TypeError && fetchError.message.includes('fetch')
        };
        console.error('❌ PARENT API - getParentGrades - Erreur fetch:', JSON.stringify(fetchErrorData, null, 2));
        throw fetchError;
      }

      // Logs de la réponse en un seul objet JSON
      const responseData = {
        status: response.status,
        response_ok: response.ok,
        response_body: data,
        data_count: data?.data ? (Array.isArray(data.data) ? data.data.length : 0) : 0
      };
      console.log('🔍 PARENT API - getParentGrades - Réponse reçue:', JSON.stringify(responseData, null, 2));

      if (!response.ok) {
        const errorMessage = data?.message || data?.error || response.statusText || `HTTP error! status: ${response.status}`;
        const httpErrorData = {
          status: response.status,
          status_text: response.statusText,
          error_message: errorMessage,
          response_body: data
        };
        console.error('❌ PARENT API - getParentGrades - Erreur HTTP:', JSON.stringify(httpErrorData, null, 2));
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401) {
          this._handleAuthError(response, errorMessage);
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(errorMessage);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      const errorData = {
        error: error.message,
        error_type: error.constructor.name,
        stack: error.stack,
        name: error.name
      };
      console.error('❌ PARENT API - getParentGrades - Erreur:', JSON.stringify(errorData, null, 2));
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des notes'
      };
    }
  }

  // ==================== PARENT QURAN RECORDS ====================
  
  // Récupérer les enregistrements du Coran des enfants
  async getParentQuranRecords(forceRefresh = false) {
    try {
      this._ensureToken();
      
      const { parentIdFromToken, decodedTokenInfo } = this._decodeToken();
      const currentUser = this.getCurrentUser();
      const parentIdFromStorage = currentUser?.id || currentUser?.user_id || currentUser?.userId;
      
      // Ajouter un paramètre de cache-busting si on force le refresh
      const cacheBuster = forceRefresh ? `?_t=${Date.now()}` : '';
      const url = `${this.baseUrl}/api/parent/quran-records${cacheBuster}`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` }),
        // Forcer le rechargement en désactivant le cache du navigateur
        ...(forceRefresh && { 'Cache-Control': 'no-cache, no-store, must-revalidate', 'Pragma': 'no-cache', 'Expires': '0' })
      };
      
      // Logs des paramètres en un seul objet JSON
      const requestParams = {
        method: 'GET',
        url: url,
        headers: headers,
        token_present: !!this.token,
        token_preview: this.token ? this.token.substring(0, 20) + '...' : null,
        parent_id_from_token: parentIdFromToken,
        parent_id_from_storage: parentIdFromStorage,
        decoded_token: decodedTokenInfo,
        base_url: this.baseUrl,
        parameters: null,
        note: 'Le backend extrait automatiquement parentUserId depuis req.user.id du token JWT'
      };
      console.log('🔍 PARENT API - getParentQuranRecords - Paramètres envoyés:', JSON.stringify(requestParams, null, 2));
      
      let response;
      let responseText;
      let data;
      
      try {
        response = await fetch(url, {
          method: 'GET',
          headers: headers,
          // Forcer le rechargement en désactivant le cache du navigateur
          cache: forceRefresh ? 'no-store' : 'default'
        });
        
        responseText = await response.text();
        
        try {
          data = responseText ? JSON.parse(responseText) : null;
        } catch (parseError) {
          const parseErrorData = {
            error: 'Erreur lors du parsing JSON',
            parse_error: parseError.message,
            response_text: responseText,
            status: response.status
          };
          console.error('❌ PARENT API - getParentQuranRecords - Erreur parsing JSON:', JSON.stringify(parseErrorData, null, 2));
          throw new Error(`Réponse non-JSON du serveur (status: ${response.status}): ${responseText.substring(0, 100)}`);
        }
      } catch (fetchError) {
        const fetchErrorData = {
          error: 'Erreur lors de l\'appel fetch',
          fetch_error: fetchError.message,
          error_type: fetchError.constructor.name,
          url: url,
          network_error: fetchError instanceof TypeError && fetchError.message.includes('fetch')
        };
        console.error('❌ PARENT API - getParentQuranRecords - Erreur fetch:', JSON.stringify(fetchErrorData, null, 2));
        throw fetchError;
      }

      // Log toutes les valeurs de evalNewHifz et evalOldHifzMurajah
      if (data?.data && Array.isArray(data.data)) {
        console.log('🔍 PARENT API - Toutes les évaluations reçues:', data.data.map(r => ({
          id: r.id,
          studentName: r.studentName,
          evalNewHifz: r.evalNewHifz,
          evalOldHifzMurajah: r.evalOldHifzMurajah,
          evalNewHifzType: typeof r.evalNewHifz,
          evalOldHifzMurajahType: typeof r.evalOldHifzMurajah
        })));
      }

      if (!response.ok) {
        const errorMessage = data?.message || data?.error || response.statusText || `HTTP error! status: ${response.status}`;
        const httpErrorData = {
          status: response.status,
          status_text: response.statusText,
          error_message: errorMessage,
          response_body: data
        };
        console.error('❌ PARENT API - getParentQuranRecords - Erreur HTTP:', JSON.stringify(httpErrorData, null, 2));
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401) {
          this._handleAuthError(response, errorMessage);
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(errorMessage);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      const errorData = {
        error: error.message,
        error_type: error.constructor.name,
        stack: error.stack,
        name: error.name
      };
      console.error('❌ PARENT API - getParentQuranRecords - Erreur:', JSON.stringify(errorData, null, 2));
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des enregistrements du Coran'
      };
    }
  }

  // ==================== PARENT NOTES ====================
  
  // Récupérer les notes générales des enfants
  async getParentNotes(lang = 'ar') {
    try {
      this._ensureToken();
      
      const { parentIdFromToken, decodedTokenInfo } = this._decodeToken();
      const currentUser = this.getCurrentUser();
      const parentIdFromStorage = currentUser?.id || currentUser?.user_id || currentUser?.userId;
      const locale = lang === 'fr' || lang === 'en' ? lang : 'ar';
      
      const url = `${this.baseUrl}/api/parent/notes?lang=${encodeURIComponent(locale)}`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` })
      };
      
      // Logs des paramètres en un seul objet JSON
      const requestParams = {
        method: 'GET',
        url: url,
        headers: headers,
        token_present: !!this.token,
        token_preview: this.token ? this.token.substring(0, 20) + '...' : null,
        parent_id_from_token: parentIdFromToken,
        parent_id_from_storage: parentIdFromStorage,
        decoded_token: decodedTokenInfo,
        base_url: this.baseUrl,
        parameters: null,
        note: 'Le backend extrait automatiquement parentUserId depuis req.user.id du token JWT'
      };
      console.log('🔍 PARENT API - getParentNotes - Paramètres envoyés:', JSON.stringify(requestParams, null, 2));
      
      let response;
      let responseText;
      let data;
      
      try {
        response = await fetch(url, {
          method: 'GET',
          headers: headers
        });
        
        responseText = await response.text();
        
        const rawResponseData = {
          status: response.status,
          status_text: response.statusText,
          response_ok: response.ok,
          headers: Object.fromEntries(response.headers.entries()),
          response_text: responseText
        };
        console.log('🔍 PARENT API - getParentNotes - Réponse brute:', JSON.stringify(rawResponseData, null, 2));
        
        try {
          data = responseText ? JSON.parse(responseText) : null;
        } catch (parseError) {
          const parseErrorData = {
            error: 'Erreur lors du parsing JSON',
            parse_error: parseError.message,
            response_text: responseText,
            status: response.status
          };
          console.error('❌ PARENT API - getParentNotes - Erreur parsing JSON:', JSON.stringify(parseErrorData, null, 2));
          throw new Error(`Réponse non-JSON du serveur (status: ${response.status}): ${responseText.substring(0, 100)}`);
        }
      } catch (fetchError) {
        const fetchErrorData = {
          error: 'Erreur lors de l\'appel fetch',
          fetch_error: fetchError.message,
          error_type: fetchError.constructor.name,
          url: url,
          network_error: fetchError instanceof TypeError && fetchError.message.includes('fetch')
        };
        console.error('❌ PARENT API - getParentNotes - Erreur fetch:', JSON.stringify(fetchErrorData, null, 2));
        throw fetchError;
      }

      // Logs de la réponse en un seul objet JSON
      const responseData = {
        status: response.status,
        response_ok: response.ok,
        response_body: data,
        data_count: data?.data ? (Array.isArray(data.data) ? data.data.length : 0) : 0
      };
      console.log('🔍 PARENT API - getParentNotes - Réponse reçue:', JSON.stringify(responseData, null, 2));

      if (!response.ok) {
        const errorMessage = data?.message || data?.error || response.statusText || `HTTP error! status: ${response.status}`;
        const httpErrorData = {
          status: response.status,
          status_text: response.statusText,
          error_message: errorMessage,
          response_body: data
        };
        console.error('❌ PARENT API - getParentNotes - Erreur HTTP:', JSON.stringify(httpErrorData, null, 2));
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401) {
          this._handleAuthError(response, errorMessage);
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(errorMessage);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      const errorData = {
        error: error.message,
        error_type: error.constructor.name,
        stack: error.stack,
        name: error.name
      };
      console.error('❌ PARENT API - getParentNotes - Erreur:', JSON.stringify(errorData, null, 2));
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des notes'
      };
    }
  }

  // ==================== PARENT STATS ====================
  
  // Récupérer les statistiques du parent
  async getParentStats() {
    try {
      this._ensureToken();
      
      const { parentIdFromToken, decodedTokenInfo } = this._decodeToken();
      const currentUser = this.getCurrentUser();
      const parentIdFromStorage = currentUser?.id || currentUser?.user_id || currentUser?.userId;
      
      const url = `${this.baseUrl}/api/parent/stats`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` })
      };
      
      // Logs des paramètres en un seul objet JSON
      const requestParams = {
        method: 'GET',
        url: url,
        headers: headers,
        token_present: !!this.token,
        token_preview: this.token ? this.token.substring(0, 20) + '...' : null,
        parent_id_from_token: parentIdFromToken,
        parent_id_from_storage: parentIdFromStorage,
        decoded_token: decodedTokenInfo,
        base_url: this.baseUrl,
        parameters: null,
        note: 'Le backend extrait automatiquement parentUserId depuis req.user.id du token JWT'
      };
      console.log('🔍 PARENT API - getParentStats - Paramètres envoyés:', JSON.stringify(requestParams, null, 2));
      
      let response;
      let responseText;
      let data;
      
      try {
        response = await fetch(url, {
          method: 'GET',
          headers: headers
        });
        
        // Récupérer le texte brut avant de parser JSON
        responseText = await response.text();
        
        // Logs de la réponse brute
        const rawResponseData = {
          status: response.status,
          status_text: response.statusText,
          response_ok: response.ok,
          headers: Object.fromEntries(response.headers.entries()),
          response_text: responseText
        };
        console.log('🔍 PARENT API - getParentStats - Réponse brute:', JSON.stringify(rawResponseData, null, 2));
        
        // Essayer de parser le JSON
        try {
          data = responseText ? JSON.parse(responseText) : null;
        } catch (parseError) {
          const parseErrorData = {
            error: 'Erreur lors du parsing JSON',
            parse_error: parseError.message,
            response_text: responseText,
            status: response.status
          };
          console.error('❌ PARENT API - getParentStats - Erreur parsing JSON:', JSON.stringify(parseErrorData, null, 2));
          throw new Error(`Réponse non-JSON du serveur (status: ${response.status}): ${responseText.substring(0, 100)}`);
        }
      } catch (fetchError) {
        const fetchErrorData = {
          error: 'Erreur lors de l\'appel fetch',
          fetch_error: fetchError.message,
          error_type: fetchError.constructor.name,
          url: url,
          network_error: fetchError instanceof TypeError && fetchError.message.includes('fetch')
        };
        console.error('❌ PARENT API - getParentStats - Erreur fetch:', JSON.stringify(fetchErrorData, null, 2));
        throw fetchError;
      }

      // Logs de la réponse parsée en un seul objet JSON
      const responseData = {
        status: response.status,
        response_ok: response.ok,
        response_body: data,
        data_count: data?.data ? (Array.isArray(data.data) ? data.data.length : (data.data ? 1 : 0)) : 0
      };
      console.log('🔍 PARENT API - getParentStats - Réponse reçue:', JSON.stringify(responseData, null, 2));

      if (!response.ok) {
        const errorMessage = data?.message || data?.error || response.statusText || `HTTP error! status: ${response.status}`;
        const httpErrorData = {
          status: response.status,
          status_text: response.statusText,
          error_message: errorMessage,
          response_body: data
        };
        console.error('❌ PARENT API - getParentStats - Erreur HTTP:', JSON.stringify(httpErrorData, null, 2));
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401) {
          this._handleAuthError(response, errorMessage);
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(errorMessage);
      }

      return {
        success: true,
        data: data?.data || data
      };
    } catch (error) {
      const errorData = {
        error: error.message,
        error_type: error.constructor.name,
        stack: error.stack,
        name: error.name
      };
      console.error('❌ PARENT API - getParentStats - Erreur:', JSON.stringify(errorData, null, 2));
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des statistiques'
      };
    }
  }

  // ==================== PARENT MESSAGES ====================
  
  // Récupérer les messages du parent
  async getParentMessages(filter = null) {
    try {
      this._ensureToken();
      
      const { parentIdFromToken, decodedTokenInfo } = this._decodeToken();
      const currentUser = this.getCurrentUser();
      const parentIdFromStorage = currentUser?.id || currentUser?.user_id || currentUser?.userId;
      
      // Support du filtre ?filter=sent|received|all
      const url = filter 
        ? `${this.baseUrl}/api/parent/messages?filter=${filter}`
        : `${this.baseUrl}/api/parent/messages`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` })
      };
      
      // Logs des paramètres en un seul objet JSON
      const requestParams = {
        method: 'GET',
        url: url,
        headers: headers,
        token_present: !!this.token,
        token_preview: this.token ? this.token.substring(0, 20) + '...' : null,
        parent_id_from_token: parentIdFromToken,
        parent_id_from_storage: parentIdFromStorage,
        decoded_token: decodedTokenInfo,
        base_url: this.baseUrl,
        parameters: null,
        note: 'Le backend extrait automatiquement parentUserId depuis req.user.id du token JWT'
      };
      console.log('🔍 PARENT API - getParentMessages - Paramètres envoyés:', JSON.stringify(requestParams, null, 2));
      
      let response;
      let responseText;
      let data;
      
      try {
        response = await fetch(url, {
          method: 'GET',
          headers: headers
        });
        
        responseText = await response.text();
        
        const rawResponseData = {
          status: response.status,
          status_text: response.statusText,
          response_ok: response.ok,
          headers: Object.fromEntries(response.headers.entries()),
          response_text: responseText
        };
        console.log('🔍 PARENT API - getParentMessages - Réponse brute:', JSON.stringify(rawResponseData, null, 2));
        
        try {
          data = responseText ? JSON.parse(responseText) : null;
        } catch (parseError) {
          const parseErrorData = {
            error: 'Erreur lors du parsing JSON',
            parse_error: parseError.message,
            response_text: responseText,
            status: response.status
          };
          console.error('❌ PARENT API - getParentMessages - Erreur parsing JSON:', JSON.stringify(parseErrorData, null, 2));
          throw new Error(`Réponse non-JSON du serveur (status: ${response.status}): ${responseText.substring(0, 100)}`);
        }
      } catch (fetchError) {
        const fetchErrorData = {
          error: 'Erreur lors de l\'appel fetch',
          fetch_error: fetchError.message,
          error_type: fetchError.constructor.name,
          url: url,
          network_error: fetchError instanceof TypeError && fetchError.message.includes('fetch')
        };
        console.error('❌ PARENT API - getParentMessages - Erreur fetch:', JSON.stringify(fetchErrorData, null, 2));
        throw fetchError;
      }

      // Logs de la réponse parsée en un seul objet JSON
      const responseData = {
        status: response.status,
        response_ok: response.ok,
        response_body: data,
        data_count: data?.data ? (Array.isArray(data.data) ? data.data.length : 0) : 0
      };
      console.log('🔍 PARENT API - getParentMessages - Réponse reçue:', JSON.stringify(responseData, null, 2));

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401) {
          this._handleAuthError(response, data?.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        const errorMessage = data?.message || data?.error || response.statusText || `HTTP error! status: ${response.status}`;
        const httpErrorData = {
          status: response.status,
          status_text: response.statusText,
          error_message: errorMessage,
          response_body: data
        };
        console.error('❌ PARENT API - getParentMessages - Erreur HTTP:', JSON.stringify(httpErrorData, null, 2));
        throw new Error(errorMessage);
      }

      // NOUVEAU SCHÉMA: Parser recipient_ids si c'est une string JSON
      // Utiliser created_date au lieu de sent_date/sent_time
      // student_gender et student_label sont maintenant inclus dans la réponse
      const messages = (data?.data || data || []).map(msg => ({
        ...msg,
        // Parser recipient_ids si c'est une string
        recipient_ids: typeof msg.recipient_ids === 'string' 
          ? (msg.recipient_ids ? JSON.parse(msg.recipient_ids) : [])
          : (msg.recipient_ids || []),
        // Utiliser created_date pour la date/heure (sent_date et sent_time n'existent plus)
        // ✅ rendues dans le fuseau de l'ASSOCIATION (`default_timezone`), pas dans celui du poste
        sent_date: msg.created_date ? dateStrInTz(new Date(msg.created_date), getAppTimezone()) : null,
        sent_time: msg.created_date ? formatTimeInTz(new Date(msg.created_date), getAppTimezone(), 'fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }) : null,
        // student_gender et student_label sont maintenant inclus dans la réponse (peuvent être null)
        student_gender: msg.student_gender || null,
        student_label: msg.student_label || null,
        // is_read n'existe plus dans le nouveau schéma
        is_read: false
      }));

      return {
        success: true,
        data: messages
      };
    } catch (error) {
      const errorData = {
        error: error.message,
        error_type: error.constructor.name,
        stack: error.stack,
        name: error.name
      };
      console.error('❌ PARENT API - getParentMessages - Erreur:', JSON.stringify(errorData, null, 2));
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des messages'
      };
    }
  }

  // Récupérer les administrateurs disponibles
  async getAdmins() {
    try {
      this._ensureToken();
      
      const url = `${this.baseUrl}/api/admin/admins`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` })
      };
      
      console.log('🔍 PARENT API - getAdmins - URL:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: headers
      });

      const data = await response.json();
      
      console.log('🔍 PARENT API - getAdmins - Status:', response.status);
      console.log('🔍 PARENT API - getAdmins - Response:', JSON.stringify(data, null, 2));

      if (!response.ok) {
        console.error('❌ PARENT API - getAdmins - Erreur HTTP:', response.status, data);
        // En cas d'erreur, retourner un tableau vide
        return {
          success: true,
          data: []
        };
      }

      return {
        success: true,
        data: data.data || data || []
      };
    } catch (error) {
      console.error('❌ PARENT API - getAdmins - Erreur:', error);
      // En cas d'erreur, retourner un tableau vide pour ne pas bloquer l'interface
      return {
        success: true,
        data: []
      };
    }
  }

  // Récupérer les professeurs d'un étudiant
  async getStudentTeachers(studentId) {
    try {
      this._ensureToken();
      
      const url = `${this.baseUrl}/api/parent/students/${studentId}/teachers`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` })
      };
      
      console.log('🔍 PARENT API - getStudentTeachers - URL:', url);
      console.log('🔍 PARENT API - getStudentTeachers - studentId:', studentId);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: headers
      });

      const data = await response.json();
      
      console.log('🔍 PARENT API - getStudentTeachers - Status:', response.status);
      console.log('🔍 PARENT API - getStudentTeachers - Response:', JSON.stringify(data, null, 2));

      if (!response.ok) {
        console.error('❌ PARENT API - getStudentTeachers - Erreur HTTP:', response.status, data);
        // Si l'endpoint n'existe pas (404), retourner un tableau vide au lieu d'erreur
        if (response.status === 404) {
          console.warn('⚠️ PARENT API - getStudentTeachers - Endpoint non trouvé, retour d\'un tableau vide');
          return {
            success: true,
            data: []
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data || []
      };
    } catch (error) {
      console.error('❌ PARENT API - getStudentTeachers - Erreur:', error);
      // En cas d'erreur, retourner un tableau vide pour ne pas bloquer l'interface
      return {
        success: true,
        data: [],
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des professeurs'
      };
    }
  }

  // Envoyer un message depuis le parent vers un enseignant ou un administrateur
  async sendParentMessage(messageData) {
    try {
      this._ensureToken();
      
      const { parentIdFromToken, decodedTokenInfo } = this._decodeToken();
      const currentUser = this.getCurrentUser();
      const parentIdFromStorage = currentUser?.id || currentUser?.user_id || currentUser?.userId;
      
      // Le backend exige un sujet, fournir une valeur par défaut si vide
      const subject = messageData.subject?.trim() || 'رسالة من ولي الأمر';
      
      // NOUVEAU SCHÉMA: Gérer recipient_type='admin' ou 'specific'
      const recipientType = messageData.recipient_type || 'specific';
      
      // Validation selon recipient_type
      if (recipientType === 'specific') {
        // Pour 'specific', recipient_ids est OBLIGATOIRE et non vide
        if (!messageData.recipient_ids || !Array.isArray(messageData.recipient_ids) || messageData.recipient_ids.length === 0) {
          throw new Error('Veuillez sélectionner au moins un destinataire (professeur)');
        }
      }
      // Pour 'admin', recipient_ids peut être vide (uniquement admin) ou non vide (admin + profs)
      
      const backendData = {
        recipient_type: recipientType, // 'admin' ou 'specific'
        content: messageData.message || messageData.content,
        priority: messageData.priority || 'normal',
        subject: subject // Toujours fournir un sujet (obligatoire côté backend)
      };
      
      // Ajouter student_id si fourni (optionnel selon la nouvelle API)
      // Si studentId est fourni et valide, l'utiliser, sinon ne pas l'inclure (sera null en base)
      if (messageData.studentId !== undefined && messageData.studentId !== null && messageData.studentId !== '' && messageData.studentId !== 0) {
        backendData.student_id = parseInt(messageData.studentId);
      }
      // Si studentId est 0 ou non fourni, ne pas inclure student_id (sera null en base, ce qui est correct)
      
      // Ajouter recipient_ids seulement s'il est fourni et non vide
      // Pour 'admin' avec recipient_ids vide, le backend enverra uniquement aux admins
      // Pour 'admin' avec recipient_ids non vide, le backend enverra aux admins + profs sélectionnés
      // Pour 'specific', recipient_ids est obligatoire (déjà validé)
      if (messageData.recipient_ids && Array.isArray(messageData.recipient_ids) && messageData.recipient_ids.length > 0) {
        backendData.recipient_ids = messageData.recipient_ids.map(id => parseInt(id)).filter(id => id > 0);
      }
      
      const url = `${this.baseUrl}/api/parent/send-message`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` })
      };
      
      // Logs des paramètres en un seul objet JSON
      const requestParams = {
        method: 'POST',
        url: url,
        headers: headers,
        token_present: !!this.token,
        token_preview: this.token ? this.token.substring(0, 20) + '...' : null,
        parent_id_from_token: parentIdFromToken,
        parent_id_from_storage: parentIdFromStorage,
        decoded_token: decodedTokenInfo,
        base_url: this.baseUrl,
        parameters: backendData,
        note: 'Le backend extrait automatiquement parentUserId depuis req.user.id du token JWT'
      };
      console.log('🔍 PARENT API - sendParentMessage - Paramètres envoyés:', JSON.stringify(requestParams, null, 2));
      
      let response;
      let responseText;
      let data;
      
      try {
        response = await fetch(url, {
          method: 'POST',
          headers: headers,
          body: JSON.stringify(backendData)
        });
        
        responseText = await response.text();
        
        const rawResponseData = {
          status: response.status,
          status_text: response.statusText,
          response_ok: response.ok,
          headers: Object.fromEntries(response.headers.entries()),
          response_text: responseText
        };
        console.log('🔍 PARENT API - sendParentMessage - Réponse brute:', JSON.stringify(rawResponseData, null, 2));
        
        try {
          data = responseText ? JSON.parse(responseText) : null;
        } catch (parseError) {
          const parseErrorData = {
            error: 'Erreur lors du parsing JSON',
            parse_error: parseError.message,
            response_text: responseText,
            status: response.status
          };
          console.error('❌ PARENT API - sendParentMessage - Erreur parsing JSON:', JSON.stringify(parseErrorData, null, 2));
          throw new Error(`Réponse non-JSON du serveur (status: ${response.status}): ${responseText.substring(0, 100)}`);
        }
      } catch (fetchError) {
        const fetchErrorData = {
          error: 'Erreur lors de l\'appel fetch',
          fetch_error: fetchError.message,
          error_type: fetchError.constructor.name,
          url: url,
          network_error: fetchError instanceof TypeError && fetchError.message.includes('fetch')
        };
        console.error('❌ PARENT API - sendParentMessage - Erreur fetch:', JSON.stringify(fetchErrorData, null, 2));
        throw fetchError;
      }

      // Logs de la réponse parsée en un seul objet JSON
      const responseData = {
        status: response.status,
        response_ok: response.ok,
        response_body: data,
        data_count: data?.data ? (Array.isArray(data.data) ? data.data.length : 1) : 0
      };
      console.log('🔍 PARENT API - sendParentMessage - Réponse reçue:', JSON.stringify(responseData, null, 2));

      if (!response.ok) {
        const errorMessage = data?.message || data?.error || response.statusText || `HTTP error! status: ${response.status}`;
        const httpErrorData = {
          status: response.status,
          status_text: response.statusText,
          error_message: errorMessage,
          response_body: data
        };
        console.error('❌ PARENT API - sendParentMessage - Erreur HTTP:', JSON.stringify(httpErrorData, null, 2));
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401) {
          this._handleAuthError(response, errorMessage);
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(errorMessage);
      }

      return {
        success: true,
        data: data?.data || data
      };
    } catch (error) {
      const errorData = {
        error: error.message,
        error_type: error.constructor.name,
        stack: error.stack,
        name: error.name
      };
      console.error('❌ PARENT API - sendParentMessage - Erreur:', JSON.stringify(errorData, null, 2));
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de l\'envoi du message'
      };
    }
  }

  // ==================== PASSWORD CHANGE ====================
  
  // Changer le mot de passe du parent
  async changePassword(passwordData) {
    try {
      this._ensureToken();
      
      const url = `${this.baseUrl}/api/parent/change-password`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` })
      };
      
      console.log('🔍 PARENT API - changePassword - URL complète:', url);
      console.log('🔍 PARENT API - changePassword - Méthode: PUT');
      console.log('🔍 PARENT API - changePassword - Headers:', headers);
      console.log('🔍 PARENT API - changePassword - Token présent:', !!this.token);
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: headers,
        body: JSON.stringify({
          current_password: passwordData.oldPassword,
          new_password: passwordData.newPassword
        })
      });

      const data = await response.json();
      
      console.log('🔍 PARENT API - changePassword - Status:', response.status);
      console.log('🔍 PARENT API - changePassword - Response:', data);

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data,
        message: data.message
      };
    } catch (error) {
      console.error('❌ PARENT API - changePassword - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors du changement de mot de passe'
      };
    }
  }

  // ==================== QURAN RECORDS - UPDATE REPEAT NEW HIFZ ====================
  
  // Modifier uniquement repeatNewHifz d'un enregistrement Coran
  async updateRepeatNewHifz(recordId, repeatNewHifz) {
    this._ensureToken();
    return await ApiServiceShared.updateRepeatNewHifz(
      recordId, 
      repeatNewHifz, 
      this.token, 
      this._handleAuthError.bind(this)
    );
  }

  // ==================== QURAN NEXT SESSION ====================
  
  // Récupérer la session suivante d'un étudiant
  async getNextSessionForStudent(studentId) {
    try {
      this._ensureToken();
      
      const url = `${this.baseUrl}/api/quran-next-session/student/${studentId}`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` })
      };
      
      const response = await fetch(url, {
        method: 'GET',
        headers: headers
      });
      
      const data = await response.json();
      
      console.log('🔍 PARENT API - getNextSessionForStudent - Réponse complète:', {
        status: response.status,
        ok: response.ok,
        data: data,
        dataKeys: data ? Object.keys(data) : []
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          this._handleAuthError(response, data?.message || 'Token invalide');
          return {
            success: false,
            error: 'Token d\'authentification invalide'
          };
        }
        return {
          success: false,
          error: data?.message || data?.error || 'Erreur lors de la récupération de la session suivante'
        };
      }
      
      // La réponse peut être dans data.data ou directement dans data
      const sessionData = data.data || data;
      console.log('🔍 PARENT API - getNextSessionForStudent - Données de session extraites:', sessionData);
      
      return {
        success: true,
        data: sessionData || null,
        message: data.message
      };
    } catch (error) {
      console.error('❌ PARENT API - getNextSessionForStudent - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération de la session suivante'
      };
    }
  }
  
  // Créer ou mettre à jour la session suivante d'un étudiant (upsert)
  async createOrUpdateNextSession(studentId, sessionData) {
    try {
      this._ensureToken();
      
      const url = `${this.baseUrl}/api/quran-next-session`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` })
      };
      
      const backendData = {
        studentId: parseInt(studentId),
        teacherId: sessionData.teacherId ? parseInt(sessionData.teacherId) : undefined,
        startPoint: sessionData.startPoint || 'الناس',
        nextHalaqaNewHifzText: sessionData.nextHalaqaNewHifzText || null,
        nextHalaqaNewHifzRange: sessionData.nextHalaqaNewHifzRange || null,
        nextHalaqaOldHifzMurajahText: sessionData.nextHalaqaOldHifzMurajahText || null,
        nextHalaqaOldHifzMurajahRange: sessionData.nextHalaqaOldHifzMurajahRange || null,
        nextHalaqaNewHifzMurajahText: sessionData.nextHalaqaNewHifzMurajahText || null,
        nextHalaqaNewHifzMurajahRange: sessionData.nextHalaqaNewHifzMurajahRange || null,
        repeatNewHifz: sessionData.repeatNewHifz !== undefined ? parseInt(sessionData.repeatNewHifz) : 0,
        modifiedDate: sessionData.modifiedDate || new Date().toISOString()
      };
      
      const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(backendData)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        if (response.status === 401) {
          this._handleAuthError(response, data?.message || 'Token invalide');
          return {
            success: false,
            error: 'Token d\'authentification invalide'
          };
        }
        return {
          success: false,
          error: data?.message || data?.error || 'Erreur lors de la création/mise à jour de la session suivante'
        };
      }
      
      return {
        success: true,
        data: data.data || data,
        message: data.message || 'Session suivante créée/mise à jour avec succès'
      };
    } catch (error) {
      console.error('❌ PARENT API - createOrUpdateNextSession - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la création/mise à jour de la session suivante'
      };
    }
  }
  
  // Mettre à jour uniquement repeat_new_hifz dans la session suivante
  async updateRepeatNewHifzInNextSession(studentId, repeatNewHifz) {
    try {
      this._ensureToken();
      
      const repeatNewHifzValue = parseInt(repeatNewHifz);
      if (isNaN(repeatNewHifzValue) || repeatNewHifzValue < 0) {
        return {
          success: false,
          error: 'repeatNewHifz doit être un entier positif'
        };
      }
      
      const url = `${this.baseUrl}/api/quran-next-session/student/${studentId}/repeat-new-hifz`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` })
      };
      
      const backendData = {
        repeatNewHifz: repeatNewHifzValue
      };
      
      console.log('🔍 PARENT API - updateRepeatNewHifzInNextSession - Requête:', {
        studentId,
        repeatNewHifz: repeatNewHifzValue,
        url
      });
      
      const response = await fetch(url, {
        method: 'PATCH',
        headers: headers,
        body: JSON.stringify(backendData)
      });
      
      const data = await response.json();
      
      console.log('🔍 PARENT API - updateRepeatNewHifzInNextSession - Réponse:', {
        status: response.status,
        ok: response.ok,
        success: data.success,
        data: data.data
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          this._handleAuthError(response, data?.message || 'Token invalide');
          return {
            success: false,
            error: 'Token d\'authentification invalide'
          };
        }
        return {
          success: false,
          error: data?.message || data?.error || 'Erreur lors de la mise à jour de repeat_new_hifz'
        };
      }
      
      console.log('✅ PARENT API - updateRepeatNewHifzInNextSession - Succès:', repeatNewHifzValue);
      
      return {
        success: true,
        data: data.data || data,
        message: data.message || 'repeat_new_hifz mis à jour avec succès'
      };
    } catch (error) {
      console.error('❌ PARENT API - updateRepeatNewHifzInNextSession - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la mise à jour de repeat_new_hifz'
      };
    }
  }

  // ==================== UTILITY METHODS ====================
  
  // Récupérer l'utilisateur actuel depuis localStorage
  getCurrentUser() {
    try {
      const userStr = localStorage.getItem('app_user');
      if (userStr) {
        return JSON.parse(userStr);
      }
      return null;
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'utilisateur:', error);
      return null;
    }
  }

  // Déconnexion
  logout() {
    this.token = null;
    localStorage.removeItem('app_token');
    localStorage.removeItem('app_user');
    localStorage.removeItem('login_timestamp');
    console.log('✅ PARENT API - Déconnexion effectuée');
  }
}

export default ApiServiceParent;
